import { motion } from "framer-motion";
import AboutSubNav from "@/components/AboutSubNav";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { Linkedin, Lightbulb, BadgeCheck, Wrench, Trophy, ArrowDown, ArrowRight, BookOpen } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import Navbar from "@/components/Navbar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import { Button } from "@/components/ui/button";
import aboutTeam from "@/assets/about-team.jpg";
import { authorSlug } from "@/lib/authorSlug";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { getTeamT } from "@/i18n/team";

// Profile images
import profileBartVA from "@/assets/profiles/bart-van-acker.png";
import profileElly from "@/assets/profiles/elly-de-bruyn.png";
import profileMartijn from "@/assets/profiles/martijn-reniers.png";
import profileBartC from "@/assets/profiles/bart-crommelinck.png";
import profileFilip from "@/assets/profiles/filip-dosselaere.png";
import profileBjorn from "@/assets/profiles/bjorn-aalbers.png";
import profileTom from "@/assets/profiles/tom-lokermans.png";
import profileJo from "@/assets/profiles/jo-doucet.png";
import profileJonathan from "@/assets/profiles/jonathan-boel.png";
import profileDiane from "@/assets/profiles/diane-fairweather.png";
import profileAlmudena from "@/assets/profiles/almudena-del-castillo.png";
import profileAngeles from "@/assets/profiles/angeles-escarti-nebot.png";
import profileYves from "@/assets/profiles/yves-peeraer.png";
import profileAnneSophie from "@/assets/profiles/anne-sophie-grell.png";
import profilePieterSmits from "@/assets/profiles/pieter-smits.png";
import profileKirsten from "@/assets/profiles/kirsten-van-garsse.png";
import teamGroupPhoto from "@/assets/team/group-photo.jpg";
import profileTiago from "@/assets/profiles/tiago-lourenco.png";

/* ───────── types ───────── */
type TeamMember = { name: string; role: string; image: string; linkedin?: string; slug: string; ringColors?: [string, string] };

/* ───────── member card ───────── */
const MemberCard = ({ member, index, authorAvatar, prefix }: { member: TeamMember; index: number; authorAvatar?: string | null; prefix: string }) => {
  const photo = authorAvatar || member.image;
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: index * 0.06 }} className="group text-center">
      <div className="relative mx-auto mb-4 aspect-square w-full max-w-[10rem] sm:max-w-[12rem]">
        {member.ringColors ? (
          <div className="absolute inset-0 rounded-full" style={{ background: `linear-gradient(135deg, ${member.ringColors[0]}, ${member.ringColors[1]})`, padding: '4px' }}>
            <div className="w-full h-full rounded-full bg-background" />
          </div>
        ) : null}
        <Link to={`${prefix}/author/${member.slug}`} className={`block rounded-full overflow-hidden absolute bg-muted ${member.ringColors ? 'inset-[6px]' : 'inset-0'}`}>
          <img src={photo} alt={member.name} className="w-full h-full object-cover scale-[1.08] transition-transform duration-300 group-hover:scale-[1.13]" />
        </Link>
        {member.linkedin && (
          <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="absolute -bottom-1 right-2 w-8 h-8 rounded-full bg-primary shadow-md flex items-center justify-center text-primary-foreground hover:bg-accent transition-colors" aria-label={`${member.name} on LinkedIn`}>
            <Linkedin size={14} fill="currentColor" strokeWidth={0} />
          </a>
        )}
      </div>
      <Link to={`${prefix}/author/${member.slug}`} className="text-base font-bold text-foreground hover:text-accent transition-colors">{member.name}</Link>
      <p className="text-sm text-muted-foreground">{member.role}</p>
    </motion.div>
  );
};

const WHY_ICONS = [Lightbulb, BadgeCheck, Wrench, Trophy];

/* ═══════════════════ MAIN PAGE ═══════════════════ */
const Team = () => {
  const lang = useLanguage();
  const prefix = langPrefix(lang);
  const t = getTeamT(lang);
  const aboutPath = lang === "es" ? "/es/sobre-qbd" : lang === "zh-cn" ? "/zh-cn/about-qbd" : "/en/about-qbd";
  const homeLabel = lang === "es" ? "Inicio" : lang === "zh-cn" ? "首页" : "Home";

  // Role translations (es + zh-cn)
  const roleMap: Record<string, { es?: string; zh?: string }> = {
    "Chief Executive Officer": { es: "Director Ejecutivo", zh: "首席执行官" },
    "Chief Human Resource Officer": { es: "Directora de Recursos Humanos", zh: "首席人力资源官" },
    "Chief Innovation Officer": { es: "Director de Innovación", zh: "首席创新官" },
    "Chief Operating Officer": { es: "Director de Operaciones", zh: "首席运营官" },
    "Chief Commercial Officer": { es: "Director Comercial", zh: "首席商务官" },
    "Chief Financial Officer": { es: "Director Financiero", zh: "首席财务官" },
    "Head of Global Qualification & Validation Division": { es: "Responsable Global de la División de Cualificación y Validación", zh: "全球确认与验证部门负责人" },
    "Head of Global Quality Assurance Division": { es: "Responsable Global de la División de Quality Assurance", zh: "全球质量保证部门负责人" },
    "Head of Global Clinical Division": { es: "Responsable Global de la División Clínica", zh: "全球临床部门负责人" },
    "Head of Global Vigilance Division": { es: "Responsable Global de la División de Vigilancia", zh: "全球药物警戒部门负责人" },
    "Head of Global Software Solutions & Services Division": { es: "Responsable Global de la División de Soluciones y Servicios de Software", zh: "全球软件解决方案与服务部门负责人" },
    "Head of Global Regulatory Affairs Division": { es: "Responsable Global de la División de Regulatory Affairs", zh: "全球法规事务部门负责人" },
    "Head of Global Lab Services Division": { es: "Responsable Global de la División de Servicios de Laboratorio", zh: "全球实验室服务部门负责人" },
    "Manager Regulatory Affairs – Medical Devices": { es: "Manager de Regulatory Affairs – Medical Devices", zh: "法规事务经理 – 医疗器械" },
    "Project Governance Lead": { es: "Responsable de Gobierno de Proyectos", zh: "项目治理负责人" },
    "Business Unit Manager RA IVD & Representative Services": { es: "Business Unit Manager RA IVD & Representative Services", zh: "Business Unit Manager RA IVD & Representative Services" },
    "Business Unit Manager, Regulatory Affairs Pharma & Country Manager UK": { es: "Manager de Unidad de Negocio, Regulatory Affairs Pharma y Country Manager UK", zh: "业务部门经理（制药法规事务）兼英国国家经理" },
    "Team Lead Clinical Operations - Medical Affairs Coordinator": { es: "Team Lead de Operaciones Clínicas – Coordinador de Asuntos Médicos", zh: "临床运营团队负责人 – 医学事务协调员" },
    "Managing Partner | Strategic Accounts": { es: "Socio Gestor | Cuentas Estratégicas", zh: "管理合伙人 | 战略客户" },
  };
  const trRole = (r: string) => {
    const e = roleMap[r];
    if (!e) return r;
    if (lang === "es") return e.es ?? r;
    if (lang === "zh-cn") return e.zh ?? r;
    return r;
  };
  const localizeMembers = (arr: TeamMember[]) => arr.map((m) => ({ ...m, role: trRole(m.role) }));

  const executives: TeamMember[] = [
    { name: "Bart Van Acker", role: "Chief Executive Officer", image: profileBartVA, linkedin: "https://www.linkedin.com/in/vanackerbart/", slug: "bart-van-acker", ringColors: ["#63A8FC", "#a8d4ff"] },
    { name: "Elly De Bruyn", role: "Chief Human Resource Officer", image: profileElly, linkedin: "https://www.linkedin.com/in/ellydebruyn/", slug: "elly-de-bruyn", ringColors: ["#63A8FC", "#a8d4ff"] },
    { name: "Martijn Reniers", role: "Chief Innovation Officer", image: profileMartijn, linkedin: "https://www.linkedin.com/in/martijn-reniers-b53a926/", slug: "martijn-reniers", ringColors: ["#63A8FC", "#a8d4ff"] },
    { name: "Bart Crommelinck", role: "Chief Operating Officer", image: profileBartC, linkedin: "https://www.linkedin.com/in/bartcrommelinck/", slug: "bart-crommelinck", ringColors: ["#63A8FC", "#a8d4ff"] },
    { name: "Filip Dosselaere", role: "Chief Commercial Officer", image: profileFilip, linkedin: "https://www.linkedin.com/in/fdosselaere/", slug: "filip-dosselaere", ringColors: ["#63A8FC", "#a8d4ff"] },
    { name: "Tom Lokermans", role: "Chief Financial Officer", image: profileTom, linkedin: "https://www.linkedin.com/in/tom-lokermans-6895a715/", slug: "tom-lokermans", ringColors: ["#63A8FC", "#a8d4ff"] },
    { name: "Björn Aalbers", role: "Managing Partner | Strategic Accounts", image: profileBjorn, linkedin: "https://www.linkedin.com/in/bj%C3%B6rn-aalbers/", slug: "bjorn-aalbers", ringColors: ["#63A8FC", "#a8d4ff"] },
  ];

  const divisionHeads: TeamMember[] = [
    { name: "Jo Doucet", role: "Head of Global Qualification & Validation Division", image: profileJo, linkedin: "https://www.linkedin.com/in/jodoucet/", slug: "jo-doucet", ringColors: ["#63A8FC", "#a8d4ff"] },
    { name: "Tiago de Matos Lourenço", role: "Head of Global Quality Assurance Division", image: profileTiago, linkedin: "https://www.linkedin.com/in/tiagomlourenco/", slug: "tiago-de-matos-lourenco", ringColors: ["#B07CC6", "#d9b3e8"] },
    { name: "Dr. Diane Fairweather", role: "Head of Global Clinical Division", image: profileDiane, linkedin: "https://www.linkedin.com/in/dianefairweather/", slug: "diane-fairweather", ringColors: ["#FFC810", "#FFE066"] },
    { name: "Almudena Del Castillo", role: "Head of Global Vigilance Division", image: profileAlmudena, linkedin: "https://www.linkedin.com/in/almudena-del-castillo-saiz-08b38a10/", slug: "almudena-del-castillo", ringColors: ["#FB5656", "#FF9A76"] },
    { name: "Jonathan Boel", role: "Head of Global Software Solutions & Services Division", image: profileJonathan, linkedin: "https://www.linkedin.com/in/jonathan-boel/", slug: "jonathan-boel", ringColors: ["#3CDBC0", "#7EECD9"] },
    { name: "Dr. Angeles Escarti-Nebot", role: "Head of Global Regulatory Affairs Division", image: profileAngeles, linkedin: "https://www.linkedin.com/in/aescarti/", slug: "angeles-escart-nebot", ringColors: ["#238472", "#4BB89E"] },
    { name: "Yves Peeraer", role: "Head of Global Lab Services Division", image: profileYves, linkedin: "https://www.linkedin.com/in/yves-peeraer-5407767/", slug: "yves-peeraer", ringColors: ["#5B6AD0", "#9BA3E8"] },
  ];

  const experts: TeamMember[] = [
    { name: "Anne-Sophie Grell", role: "Manager Regulatory Affairs – Medical Devices", image: profileAnneSophie, linkedin: "https://www.linkedin.com/in/asgrell/", slug: "anne-sophie-grell", ringColors: ["#63A8FC", "#a8d4ff"] },
    { name: "Pieter Smits", role: "Project Governance Lead", image: profilePieterSmits, linkedin: "https://www.linkedin.com/in/pieter-smits-4108a75a/", slug: "pieter-smits", ringColors: ["#63A8FC", "#a8d4ff"] },
    { name: "Kirsten Van Garsse", role: "Business Unit Manager RA IVD & Representative Services", image: profileKirsten, linkedin: "https://www.linkedin.com/in/kirstenvangarsse/", slug: "kirsten-van-garsse", ringColors: ["#63A8FC", "#a8d4ff"] },
    { name: "Kristof Vanschoonbeek", role: "Team Lead Clinical Operations - Medical Affairs Coordinator", image: "/avatars/kristof-vanschoonbeek.png", linkedin: "https://www.linkedin.com/in/kristof-vanschoonbeek-a07aa710/", slug: "kristof-vanschoonbeek", ringColors: ["#63A8FC", "#a8d4ff"] },
  ];

  const allSlugs = [...new Set([...executives, ...divisionHeads, ...experts].map((m) => m.slug))];

  const { data: authors } = useQuery({
    queryKey: ["team-authors", allSlugs],
    queryFn: async () => {
      const { data, error } = await supabase.from("authors").select("slug, avatar_url").in("slug", allSlugs);
      if (error) throw error;
      return data;
    },
  });

  const avatarMap = new Map(authors?.map((a) => [a.slug, a.avatar_url]) ?? []);

  return (
    <>
      <SEOHead
        title={t.seo.title}
        description={t.seo.description}
        canonical={`https://qbdgroup.com${lang === "es" ? "/es/sobre-qbd/equipo" : lang === "zh-cn" ? "/zh-cn/about-qbd/team" : "/en/about-qbd/team"}`}
        ogImage={aboutTeam}
      />
      <BreadcrumbJsonLd items={[{ name: t.breadcrumb.about, path: aboutPath }, { name: t.breadcrumb.team }]} />

      <ScrollToTop />
      <Navbar />

      <main>
        {/* ── HERO ── */}
        <section className="relative overflow-hidden bg-primary pt-36 sm:pt-44 pb-20 sm:pb-28 min-h-[420px] sm:min-h-[480px]">
          <div className="absolute inset-0">
            <img src={aboutTeam} alt="" className="h-full w-full object-cover" aria-hidden="true" />
            <div className="absolute inset-0 bg-primary/85" />
          </div>

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-2xl">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <nav className="mb-6 flex items-center gap-2 text-sm text-primary-foreground/50">
                  <a href={lang === "es" ? "/es" : lang === "zh-cn" ? "/zh-cn" : "/"} className="hover:text-primary-foreground/70 transition-colors">{homeLabel}</a>
                  <span>/</span>
                  <a href={aboutPath} className="hover:text-primary-foreground/70 transition-colors">{t.breadcrumb.about}</a>
                  <span>/</span>
                  <span className="text-primary-foreground/80">{t.breadcrumb.team}</span>
                </nav>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-primary-foreground leading-[1.1]">{t.hero.title}</h1>
                <p className="mt-6 text-base sm:text-lg leading-relaxed text-primary-foreground/75 max-w-xl" dangerouslySetInnerHTML={{ __html: t.hero.p1 }} />
                <p className="mt-4 text-base leading-relaxed text-primary-foreground/65 max-w-xl">{t.hero.p2}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a href="#team">
                    <Button size="lg" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg px-8">
                      {t.hero.ctaLeadership} <ArrowDown size={16} className="ml-2" />
                    </Button>
                  </a>
                  <a href="#divisions">
                    <Button size="lg" variant="outline" className="rounded-full border-primary-foreground/40 text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/20 px-8">
                      {t.hero.ctaDivisions} <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <AboutSubNav />

        {/* ── EXECUTIVE TEAM ── */}
        <section id="team" className="py-20 sm:py-28 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-14">
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">{t.executive.kicker}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">{t.executive.title}</h2>
              <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">{t.executive.subtitle}</p>
            </motion.div>

            <div className="grid gap-10 grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 max-w-4xl mx-auto">
              {localizeMembers(executives).map((m, i) => (
                <MemberCard key={m.name + m.role} member={m} index={i} authorAvatar={avatarMap.get(m.slug)} prefix={prefix} />
              ))}
            </div>
          </div>
        </section>

        {/* ── GROUP PHOTO with overlay ── */}
        <section className="relative">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <img src={teamGroupPhoto} alt="QbD Group team photo" className="w-full aspect-[21/9] object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
              <p className="text-primary-foreground font-bold text-lg sm:text-2xl">{t.groupPhoto.overlay}</p>
            </div>
          </motion.div>
        </section>

        {/* ── DIVISION HEADS ── */}
        <section id="divisions" className="py-20 sm:py-28 bg-muted/60 scroll-mt-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-14">
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">{t.division.kicker}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">{t.division.title}</h2>
              <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">{t.division.subtitle}</p>
            </motion.div>

            <div className="grid gap-10 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 max-w-5xl mx-auto">
              {localizeMembers(divisionHeads).map((m, i) => (
                <MemberCard key={m.name + m.role} member={m} index={i} authorAvatar={avatarMap.get(m.slug)} prefix={prefix} />
              ))}
            </div>
          </div>
        </section>

        {/* ── EXPERTS ── */}
        <section className="py-20 sm:py-28 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-14">
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">{t.experts.kicker}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">{t.experts.title}</h2>
              <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">{t.experts.subtitle}</p>
            </motion.div>

            <div className="grid gap-10 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 max-w-5xl mx-auto">
              {localizeMembers(experts).map((m, i) => (
                <MemberCard key={m.name + m.role} member={m} index={i} authorAvatar={avatarMap.get(m.slug)} prefix={prefix} />
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY ENGAGE (separate section) ── */}
        <section className="py-20 sm:py-28 bg-muted/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-14">
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">{t.whyEngage.kicker}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">{t.whyEngage.title}</h2>
              <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">{t.whyEngage.subtitle}</p>
            </motion.div>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {t.whyEngage.items.map((v, i) => {
                const Icon = WHY_ICONS[i];
                return (
                  <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: i * 0.08 }} className="rounded-2xl border border-border/60 bg-card p-7 shadow-sm">
                    <Icon size={28} className="text-accent mb-5" strokeWidth={1.5} />
                    <h3 className="text-base font-bold text-foreground mb-2">{v.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                  </motion.div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link to={`${prefix}/resources`}>
                <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 shadow-md">
                  <BookOpen size={16} className="mr-2" /> {t.whyEngage.ctaKnowledge}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <ContactCTA />
      </main>

      <Footer />
    </>
  );
};

export default Team;
